import {
  type FishGroup,
  type BatchPatchFishGroup,
  QualityModelArgumentsTypeEnum,
  WriteQualityModelAttributeAssignmentTypeEnum,
} from 'client';
import {handleInitialFishGroupCV, handleInitialFishGroupName, handleInitialFishGroupQuality} from './fishGroupHelpers';
import {type IQualityGrading} from 'interfaces';

export const reducer = (state: IFishGroup, action: IFishGroupAction): IFishGroup => {
  switch (action.type) {
    case actionTypeEnum.setInitialState:
      if (action.fishGroups && action.fishGroups.length > 0) {
        const fishGroup = action.fishGroups[0];
        const fishGroupIds = action.fishGroups.map((fishGroup) => fishGroup.id);
        const cvAttribute = handleInitialFishGroupCV(action.fishGroups, fishGroup);
        const nameAttribute = handleInitialFishGroupName(action.fishGroups);
        const qualityModelAttribute = handleInitialFishGroupQuality(action.fishGroups, fishGroup);
        const firstFishGroup = action.fishGroups[0];
        return {
          ...state,
          fishGroupIds,
          cvAttribute,
          nameAttribute,
          qualityModelAttribute,
          fishGroup: firstFishGroup,
          fishGroups: action.fishGroups,
        };
      } else {
        return state;
      }
    case actionTypeEnum.setCustomCVValue: {
      const value = action?.CVValue !== state.cvAttribute?.value ? action?.CVValue : state.cvAttribute?.value;
      return {
        ...state,
        cvAttribute: {
          isPinned: true,
          value,
        },
        cvEdited: true,
      };
    }
    case actionTypeEnum.resetCV: {
      const value =
        state.fishGroup?.cvAttribute?.resetToValue != null ? state.fishGroup.cvAttribute.resetToValue * 100 : undefined;
      return {
        ...state,
        cvAttribute: {
          isPinned: false,
          value,
        },
      };
    }
    case actionTypeEnum.setCustomQualityModel: {
      if (action.parameters) {
        return {
          ...state,
          qualityModelAttribute: {
            assignmentType: WriteQualityModelAttributeAssignmentTypeEnum.Custom,
            customArguments: {type: QualityModelArgumentsTypeEnum.Fixed, parameters: action.parameters},
            isPinned: true,
          },
          qualityModelEdited: true,
        };
      } else {
        return state;
      }
    }
    case actionTypeEnum.setQualityModelAssignment:
      return {
        ...state,
        qualityModelAttribute: {
          assignmentType: action.qualityAssignmentType,
          isPinned: true,
        },
        qualityModelEdited: true,
      };
    case actionTypeEnum.resetQualityModel: {
      const assignmentType = state.fishGroup?.qualityModelAttribute?.assignmentTypeToResetTo ?? undefined;
      return {
        ...state,
        qualityModelAttribute: {
          isPinned: false,
          assignmentType,
        },
        qualityModelEdited: true,
      };
    }
    case actionTypeEnum.setCustomName: {
      return {
        ...state,
        nameAttribute: {
          isPinned: true,
          value: action.nameValue,
        },
        nameEdited: true,
      };
    }
    case actionTypeEnum.resetName: {
      return {
        ...state,
        nameAttribute: {
          isPinned: false,
          value: state.fishGroup?.nameAttribute?.resetToValue,
        },
        nameEdited: true,
      };
    }
    case actionTypeEnum.setProperties: {
      if (action.properties === undefined) {
        return state;
      }

      return {
        ...state,
        fishGroups: state.fishGroups.map((fishGroup) => ({
          ...fishGroup,
          propertiesAttribute: {
            ...fishGroup.propertiesAttribute,
            isPinned: true,
            value:
              action.properties?.map((propertyId) => ({
                id: propertyId,
                name:
                  fishGroup.propertiesAttribute.value?.find((propertyAttribute) => propertyAttribute.id === propertyId)
                    ?.name ?? '',
              })) ?? null,
          },
        })),
        propertiesEdited: true,
      };
    }
    case actionTypeEnum.resetProperties: {
      return {
        ...state,
        fishGroups: state.fishGroups.map((fishGroup) => ({
          ...fishGroup,
          propertiesAttribute: {
            ...fishGroup.propertiesAttribute,
            isPinned: false,
            value: fishGroup.propertiesAttribute.resetToValue,
          },
        })),
        propertiesEdited: true,
      };
    }

    default:
      return state;
  }
};

export interface IFishGroup extends BatchPatchFishGroup {
  cvEdited: boolean;
  nameEdited: boolean;
  qualityModelEdited: boolean;
  propertiesEdited: boolean;
  fishGroup?: FishGroup;
  fishGroups: FishGroup[];
}

export interface IFishGroupAction extends Partial<IFishGroup> {
  type: actionTypeEnum;
  CVValue?: number;
  parameters?: IQualityGrading;
  fishGroups?: FishGroup[];
  properties?: string[];
  qualityAssignmentType?: 'default' | 'automatic';
  nameValue?: string;
}

export enum actionTypeEnum {
  setInitialState = 'setInitialState',
  setCustomCVValue = 'setCustomCVValue',
  resetCV = 'resetCV',
  setCustomName = 'setCustomName',
  resetName = 'resetName',
  setCustomQualityModel = 'setCutomQualityModel',
  setQualityModelAssignment = 'setQualityModelAssignment',
  resetQualityModel = 'resetQualityModel',
  setProperties = 'setProperties',
  resetProperties = 'resetProperties',
}
